import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import BASE_URL from "../config";
import {
  faHouse,
  faBell,
  faCalendarCheck,
  faUser,
  faLock,
  faRightFromBracket,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";

const NavigationBar = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const location = useLocation();
  const [breadcrumb, setBreadcrumb] = useState("Home");
  const [notifications, setNotifications] = useState([]);
  const [pastDueNotifications, setPastDueNotifications] = useState([]);
  const [userData, setUserData] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [toTestTasks, setToTestTasks] = useState([]); // New state for 'TO TEST' tasks
  const [userMap, setUserMap] = useState({}); // Map userId -> username

  const hideElement =
    localStorage.getItem("token") === null ||
    localStorage.getItem("token") === "";

  useEffect(() => {
    if (hideElement) {
      setIsAuthenticated(false);
    } else {
      setIsAuthenticated(true);
    }
  }, [hideElement]);

  useEffect(() => {
    // Convert URL path into a readable breadcrumb
    const path = location.pathname
      .replace("/", "") // Remove leading slash
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/-/g, " ") // Replace dashes with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word

    setBreadcrumb(path || "Home");
  }, [location.pathname]);

  useEffect(() => {
    // Fetch user data on component mount (use token if available)
    const token = localStorage.getItem("token");
    if (!token) {
      // Redirect to login if token is not available
      window.location.href = "/login";
      return;
    }

    // Fetch the user details from the backend API
    const fetchUserData = async () => {
      try {
        const response = await fetch(`${BASE_URL}api/user/profile`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 403) {
          // Invalid token, redirect to login
          localStorage.removeItem("token"); // Clear invalid token
          window.location.href = "/login";
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setUserData(data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchUserData();

  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(
      `${BASE_URL}api/clients/query?sql=` + // ✅ Use BASE_URL
      encodeURIComponent(
        `SELECT c.name, c.email, c.createdAt, p.next_due_date 
         FROM clients c 
         JOIN payments p ON c.id = p.client_id 
         WHERE p.next_due_date IS NOT NULL 
         AND (p.status IS NULL OR p.status != 'Paid')
         AND p.next_due_date BETWEEN CURDATE() AND DATE_ADD(CURDATE(), INTERVAL 7 DAY) 
         ORDER BY p.next_due_date ASC`
      ),
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        const filteredData = data.filter((client) => {
          const dueDate = new Date(client.next_due_date);
          const today = new Date();
          const sevenDaysLater = new Date();
          sevenDaysLater.setDate(today.getDate() + 7);

          return dueDate >= today && dueDate <= sevenDaysLater;
        });

        setNotifications(filteredData);
      })
      .catch((error) => console.error("Error fetching notifications:", error));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");

    fetch(
      `${BASE_URL}api/clients/query?sql=` +
      encodeURIComponent(
        `SELECT c.name, c.email, c.createdAt, p.next_due_date 
         FROM clients c 
         JOIN payments p ON c.id = p.client_id 
         WHERE p.next_due_date IS NOT NULL 
         AND (p.status IS NULL OR p.status != 'Paid')
         AND p.next_due_date BETWEEN DATE_SUB(CURDATE(), INTERVAL 7 DAY) AND CURDATE() 
         ORDER BY p.next_due_date ASC`
      ),
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        const filteredData = data.filter((client) => {
          const dueDate = new Date(client.next_due_date);
          const today = new Date();
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(today.getDate() - 7);

          return dueDate >= sevenDaysAgo && dueDate < today;
        });

        setPastDueNotifications(filteredData);
      })
      .catch((error) => console.error("Error fetching past due notifications:", error));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    // Fetch all users to map user IDs to usernames
    fetch(`${BASE_URL}api/user/active-users`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((users) => {
        // Build a map: id -> username
        const map = {};
        users.forEach((u) => {
          map[String(u.id)] = u.username;
        });
        setUserMap(map);
      })
      .catch((error) => console.error("Error fetching users for notifications:", error));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    // Fetch tasks with status 'TO TEST'
    fetch(`${BASE_URL}api/tasks/all`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((response) => response.json())
      .then((tasks) => {
        // Filter tasks with status/list 'TO TEST'
        const toTest = tasks.filter((task) => task.list === "TO TEST");
        setToTestTasks(toTest);
      })
      .catch((error) => console.error("Error fetching 'TO TEST' tasks:", error));
  }, []);

  const formatDate = (dateString) => {
    const options = { month: "short", day: "2-digit", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const handleLogout = () => {
    localStorage.removeItem("token"); // Remove token from localStorage
    window.location.href = "/login";
  };

  const isDashboard = location.pathname === "/" || location.pathname.includes("home");

  return (
    <div
      style={{
        borderBottom: "1px solid #ddd",
        backgroundColor: "#ffffff", 
      }}
    >
      <div
        className="top-bar d-flex justify-content-between align-items-center px-3 py-2 bg-white"
        style={{
          fontFamily: "'Lato', Helvetica, Arial, Lucida, sans-serif", // Apply font family
          height: isDashboard ? "80px" : "auto", // Set height to 80px for "/" or "/home"
          width: isDashboard ? "80%" : "100%", // Set width to 80% for "/" or "/home"
          maxWidth: isDashboard ? "1080px" : "none", // Set max-width to 1080px for "/" or "/home"
          margin: isDashboard ? "0 auto" : "0", // Center align for "/" or "/home"
        }}
      >
        {isAuthenticated && (
          <div className="left">
            <>
              <Link to="/home">
                <FontAwesomeIcon icon={faHouse} style={{ color: "#22b98e" }} />
              </Link>{" "}
              &gt; {breadcrumb}
            </>
          </div>
        )}

        <div className="right d-flex align-items-center">
          {/* Dynamic Menu Buttons */}
          {isDashboard && menuItems.length > 0 && (
            <div style={{ display: "inline-flex", gap: "10px" }}>
              {menuItems.map((item) => (
                <Link
                  key={item.slug}
                  to={`/${item.slug}`}
                  className="btn text-dark text-decoration-none"
                  style={{ outline: "none" }} // Remove border outline on click
                >
                  {item.pagename}
                </Link>
              ))}
            </div>
          )}

          {/* Notifications */}
          {isAuthenticated && userData?.role === "admin" && (
            <div className="dropdown me-3">
              <button
                className="btn"
                type="button"
                id="notificationDropdown"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <FontAwesomeIcon icon={faBell} />
                {(toTestTasks.length + notifications.length + pastDueNotifications.length) > 0 && (
                  <span className="badge bg-danger ms-1">
                    {toTestTasks.length + notifications.length + pastDueNotifications.length}
                  </span>
                )}
              </button>
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby="notificationDropdown"
              >
                {toTestTasks.length === 0 && notifications.length === 0 && pastDueNotifications.length === 0 ? (
                  <li>
                    <span className="dropdown-item text-muted">
                      No notifications
                    </span>
                  </li>
                ) : (
                  <>
                    {/* Show 'TO TEST' task notifications */}
                    {toTestTasks.map((task) => (
                      <li key={`totest-${task.id}`}>
                        <span className="dropdown-item">
                          <FontAwesomeIcon icon={faCalendarCheck} />
                          <strong style={{ color: "#b22222" }}>
                            {" "}
                            {userMap[String(task.assignee)] || "Someone"}
                          </strong>{" "}
                          marked the task <strong>{task.title}</strong> as <strong>TO TEST</strong>.
                        </span>
                      </li>
                    ))}
                    {notifications.map((notification, index) => (
                      <li key={index}>
                        <span className="dropdown-item">
                          <FontAwesomeIcon icon={faCalendarCheck} />
                          <strong style={{ color: "#b22222" }}>
                            {" "}
                            {notification.name}'s
                          </strong>{" "}
                          payment is due on{" "}
                          <strong style={{ color: "#b22222" }}>
                            {formatDate(notification.next_due_date)}
                          </strong>
                        </span>
                      </li>
                    ))}
                    {pastDueNotifications.map((notification, index) => (
                      <li key={`past-${index}`}>
                        <span className="dropdown-item">
                          <FontAwesomeIcon icon={faCalendarCheck} />
                          <strong style={{ color: "#b22222" }}>
                            {" "}
                            {notification.name}'s
                          </strong>{" "}
                          payment was due on{" "}
                          <strong style={{ color: "#b22222" }}>
                            {formatDate(notification.next_due_date)}
                          </strong>
                        </span>
                      </li>
                    ))}
                  </>
                )}
              </ul>
            </div>
          )}

          {/* User Profile */}
          {isAuthenticated && (
            <div className="dropdown">
              {userData && (
                <button
                  className="btn"
                  type="button"
                  id="profileDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  {userData.username} <FontAwesomeIcon icon={faChevronDown} />
                </button>
              )}

              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby="profileDropdown"
              >
                <li> {userData && (
                  <Link className="dropdown-item" to={`/user-detail?id=${userData.id}`}>
                    <FontAwesomeIcon icon={faUser} /> My Profile
                  </Link>)}
                </li>
                <li>
                  {userData && (
                    <Link className="dropdown-item" to={`/edit-profile?id=${userData.id}`}>
                      <FontAwesomeIcon icon={faUser} /> Edit Profile
                    </Link>
                  )}
                </li>
                <li>
                  <Link className="dropdown-item" to="/change-password">
                    <FontAwesomeIcon icon={faLock} /> Change Password
                  </Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="#" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faRightFromBracket} /> Logout
                  </Link>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NavigationBar;
